import { MEAL_MACRO_DECIMAL_PLACE } from "config"
import { roundByDecimalPlace } from "utils"

export const CALORIES_PER_PROTEIN = 4
export const CALORIES_PER_FAT = 9
export const CALORIES_PER_CARBS = 4

export const getCaloriesPercentFromMacrosGram = ({
  proteinGram,
  fatGram,
  carbsGram,
}: {
  proteinGram: null | number
  fatGram: null | number
  carbsGram: null | number
}) => {
  const proteinCalories = getCaloriesFromMacroGram({
    grams: proteinGram,
    macroCaloriesPerGram: CALORIES_PER_PROTEIN,
  })
  const fatCalories = getCaloriesFromMacroGram({
    grams: fatGram,
    macroCaloriesPerGram: CALORIES_PER_FAT,
  })
  const carbsCalories = getCaloriesFromMacroGram({
    grams: carbsGram,
    macroCaloriesPerGram: CALORIES_PER_CARBS,
  })

  const totalCalories = Math.round(
    proteinCalories + fatCalories + carbsCalories,
  )

  if (totalCalories <= 0) {
    return {
      proteinPercent: 0,
      fatPercent: 0,
      carbPercent: 0,
    }
  }

  // Step 1: Get raw (float) percentages
  const proteinRaw = (proteinCalories / totalCalories) * 100
  const fatRaw = (fatCalories / totalCalories) * 100
  const carbsRaw = (carbsCalories / totalCalories) * 100

  // Step 2: Round down each and track remainder
  const rounded = [
    {
      name: "proteinPercent",
      value: Math.floor(proteinRaw),
      remainder: proteinRaw % 1,
    },
    { name: "fatPercent", value: Math.floor(fatRaw), remainder: fatRaw % 1 },
    {
      name: "carbPercent",
      value: Math.floor(carbsRaw),
      remainder: carbsRaw % 1,
    },
  ]

  // Step 3: Calculate how much is missing to 100
  let sum = rounded.reduce((acc, x) => acc + x.value, 0)
  let deficit = 100 - sum

  // Step 4: Distribute missing points to the macros with highest remainders
  rounded
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((item, i) => {
      if (i < deficit) {
        item.value += 1
      }
    })

  return {
    proteinPercent: rounded.find((x) => x.name === "proteinPercent")!.value,
    fatPercent: rounded.find((x) => x.name === "fatPercent")!.value,
    carbPercent: rounded.find((x) => x.name === "carbPercent")!.value,
  }
}

export const getCaloriesFromMacroGram = ({
  grams,
  macroCaloriesPerGram,
}: {
  grams: null | number
  macroCaloriesPerGram: number
}) => {
  if (grams === null || grams === 0) {
    return 0
  }

  return Math.round(grams * macroCaloriesPerGram)
}

export const getCaloriesFromMacrosGram = ({
  proteinGram,
  fatGram,
  carbsGram,
}: {
  proteinGram: null | number
  fatGram: null | number
  carbsGram: null | number
}) => {
  const proteinCalories = getCaloriesFromMacroGram({
    grams: proteinGram,
    macroCaloriesPerGram: CALORIES_PER_PROTEIN,
  })
  const fatCalories = getCaloriesFromMacroGram({
    grams: fatGram,
    macroCaloriesPerGram: CALORIES_PER_FAT,
  })
  const carbsCalories = getCaloriesFromMacroGram({
    grams: carbsGram,
    macroCaloriesPerGram: CALORIES_PER_CARBS,
  })

  return Math.round(proteinCalories + fatCalories + carbsCalories)
}

export const getMarcoGramFromCaloriesPercent = ({
  calories,
  percent,
  macroCaloriesPerGram,
  decimalPlaces = 0,
}: {
  calories: null | number
  percent: number
  macroCaloriesPerGram: number
  decimalPlaces?: number
}) => {
  if (percent === 0 || calories === null || calories === 0) {
    return 0
  }

  const macroCalories = (calories * percent) / 100
  return roundByDecimalPlace(
    macroCalories / macroCaloriesPerGram,
    decimalPlaces,
  )
}

export const sum = (a: null | number, b: null | number, c: null | number) => {
  const values = [a, b, c]

  const sum = values.reduce((total: number, val) => {
    return total + (typeof val === "number" ? val : 0)
  }, 0)

  return sum
}

export const roundNumber = (number: number) => {
  return Math.round(Number(number.toFixed(10)))
}
